import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  HStack,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaTasks } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getMyTasks } from "../../../api/tasksApi";
import { Panel, StatusBadge, EmptyState } from "../ui";

const priorityColorScheme = (p) => {
  switch (p) {
    case "high":
      return "red";
    case "normal":
      return "blue";
    case "low":
      return "gray";
    default:
      return "gray";
  }
};

const MyTasksWidget = ({ buId }) => {
  const { t } = useTranslation("portal");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchTasks = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await getMyTasks(
          { page: 1, pageSize: 5, status: "open" },
          { buId: buId || undefined }
        );
        if (cancelled) return;
        setTasks(res?.items || []);
        setOpenCount(typeof res?.totalItems === "number" ? res.totalItems : (res?.items?.length || 0));
      } catch {
        if (cancelled) return;
        setError(true);
        setTasks([]);
        setOpenCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTasks();
    return () => {
      cancelled = true;
    };
  }, [buId]);

  return (
    <Panel
      title={t("tasks.widget.heading")}
      count={loading ? undefined : t("tasks.widget.openCount", { count: openCount })}
      actions={
        <Button
          variant="secondary"
          size="sm"
          h="28px"
          px="10px"
          fontSize="11px"
          onClick={() => navigate("/portal/asap-pilot/tasks")}
        >
          {t("tasks.widget.viewAll")}
        </Button>
      }
    >
      {loading ? (
        <Center py="24px">
          <Spinner size="sm" color="portal.primary" />
        </Center>
      ) : error ? (
        <EmptyState
          icon={<FaTasks />}
          title={t("tasks.loadError")}
        />
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={<FaTasks />}
          title={t("tasks.widget.empty")}
          hint={t("tasks.widget.emptyHint", "You're all caught up.")}
        />
      ) : (
        <VStack align="stretch" spacing="8px" p="12px">
          {tasks.map((task) => (
            <HStack
              key={task.id}
              px="12px"
              py="10px"
              bg="portal.soft"
              borderRadius="10px"
              borderWidth="1px"
              borderColor="portal.line"
              justify="space-between"
              spacing="12px"
            >
              <Box flex="1" minW={0}>
                <Text
                  fontSize="13px"
                  fontWeight="700"
                  color="portal.text"
                  noOfLines={1}
                  sx={{ marginBottom: "2px !important" }}
                >
                  {task.title}
                </Text>
                <Text
                  fontSize="11px"
                  color="portal.muted"
                  sx={{ marginBottom: "0 !important" }}
                >
                  {task.due_date
                    ? t("tasks.widget.dueLabel", { date: task.due_date })
                    : t("tasks.widget.noDueDate")}
                </Text>
              </Box>
              <StatusBadge
                label={t(`tasks.priority.${task.priority || "normal"}`)}
                colorScheme={priorityColorScheme(task.priority)}
              />
            </HStack>
          ))}
        </VStack>
      )}
    </Panel>
  );
};

export default MyTasksWidget;
